import React, { useState, useEffect } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar } from 'recharts';
import { useAuth } from '../../../contexts/AuthContext';
import { supabase } from '../../../lib/supabase';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const RevenueChart = ({ organizationId, refreshTrigger }) => {
  const { userProfile, isAuthenticated } = useAuth();
  const [chartType, setChartType] = useState('line');
  const [timeRange, setTimeRange] = useState('6months');
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [revenueData, setRevenueData] = useState([]);

  useEffect(() => {
    if (isAuthenticated && (organizationId || userProfile?.organization_id)) {
      loadRevenueData();
    }
  }, [isAuthenticated, organizationId, userProfile?.organization_id, refreshTrigger, timeRange]);

  const loadRevenueData = async () => {
    const orgId = organizationId || userProfile?.organization_id;
    if (!orgId) return;

    try {
      setLoading(true);
      setError(null);

      // Calculate date range
      const endDate = new Date();
      const startDate = new Date();
      
      switch (timeRange) {
        case '3months':
          startDate?.setMonth(endDate?.getMonth() - 3);
          break;
        case '12months':
          startDate?.setMonth(endDate?.getMonth() - 12);
          break;
        default: // 6months
          startDate?.setMonth(endDate?.getMonth() - 6);
      }

      // Fetch revenue data from invoices
      const { data: invoices, error: invoicesError } = await supabase
        ?.from('invoices')
        ?.select('total_amount, status, issue_date, created_at')
        ?.eq('organization_id', orgId)
        ?.gte('issue_date', startDate?.toISOString()?.split('T')?.[0])
        ?.lte('issue_date', endDate?.toISOString()?.split('T')?.[0]);

      // Fetch expense data from bills
      const { data: bills, error: billsError } = await supabase
        ?.from('bills')
        ?.select('total_amount, status, issue_date, created_at')
        ?.eq('organization_id', orgId)
        ?.gte('issue_date', startDate?.toISOString()?.split('T')?.[0])
        ?.lte('issue_date', endDate?.toISOString()?.split('T')?.[0]);

      if (invoicesError || billsError) {
        console.error('Error loading revenue data:', { invoicesError, billsError });
        setError('Failed to load revenue data');
        return;
      }

      // Process monthly data
      const monthlyData = processMonthlyRevenueData(invoices || [], bills || [], startDate, endDate);
      setRevenueData(monthlyData);

    } catch (err) {
      console.error('Error loading revenue chart data:', err);
      setError('Failed to load revenue chart data');
    } finally {
      setLoading(false);
    }
  };

  const processMonthlyRevenueData = (invoices, bills, startDate, endDate) => {
    const monthlyMap = new Map();
    
    // Initialize months
    const current = new Date(startDate);
    while (current <= endDate) {
      const monthKey = `${current?.getFullYear()}-${String(current?.getMonth() + 1)?.padStart(2, '0')}`;
      const monthName = current?.toLocaleDateString('en-US', { month: 'short' });
      
      monthlyMap?.set(monthKey, {
        month: monthName,
        revenue: 0,
        expenses: 0,
        profit: 0
      });
      
      current?.setMonth(current?.getMonth() + 1);
    }

    // Process invoices for revenue
    invoices?.forEach(invoice => {
      const date = new Date(invoice?.issue_date);
      const monthKey = `${date?.getFullYear()}-${String(date?.getMonth() + 1)?.padStart(2, '0')}`;
      
      if (monthlyMap?.has(monthKey)) {
        const monthData = monthlyMap?.get(monthKey);
        if (invoice?.status === 'paid' || invoice?.status === 'partially_paid') {
          monthData.revenue += Number(invoice?.total_amount || 0);
        }
        monthlyMap?.set(monthKey, monthData);
      }
    });

    // Process bills for expenses
    bills?.forEach(bill => {
      const date = new Date(bill?.issue_date);
      const monthKey = `${date?.getFullYear()}-${String(date?.getMonth() + 1)?.padStart(2, '0')}`;
      
      if (monthlyMap?.has(monthKey)) {
        const monthData = monthlyMap?.get(monthKey);
        if (bill?.status === 'paid' || bill?.status === 'approved') {
          monthData.expenses += Number(bill?.total_amount || 0);
        }
        monthlyMap?.set(monthKey, monthData);
      }
    });

    // Calculate profit and convert to array
    return Array?.from(monthlyMap?.values())?.map(data => ({
      ...data,
      profit: data?.revenue - data?.expenses
    }));
  };

  const handleDownloadReport = async (format) => {
    setDownloadLoading(true);
    
    try {
      if (format === 'pdf') {
        await generatePDFReport();
      } else if (format === 'xls') {
        await generateXLSReport();
      }
    } catch (error) {
      console.error('Revenue chart download error:', error);
      alert(`Failed to download Revenue ${format?.toUpperCase()} report. Please try again.`);
    } finally {
      setDownloadLoading(false);
      setShowDownloadModal(false);
    }
  };

  const generatePDFReport = async () => {
    const { jsPDF } = await import('jspdf');
    const { default: autoTable } = await import('jspdf-autotable');
    
    const doc = new jsPDF();
    
    // Header
    doc?.setFontSize(20);
    doc?.text('Revenue Performance Report', 20, 20);
    
    doc?.setFontSize(12);
    doc?.text(`Generated on: ${new Date()?.toLocaleDateString('en-US', { 
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    })}`, 20, 35);
    doc?.text(`Data Period: ${timeRange} (Live Data)`, 20, 45);
    doc?.text(`Organization: ${userProfile?.organization_name || 'Current Organization'}`, 20, 55);
    
    // Revenue Performance Summary
    doc?.setFontSize(16);
    doc?.text('Revenue Performance Summary', 20, 75);
    
    const totalRevenue = revenueData?.reduce((sum, item) => sum + item?.revenue, 0);
    const totalExpenses = revenueData?.reduce((sum, item) => sum + item?.expenses, 0);
    const totalProfit = totalRevenue - totalExpenses;
    const avgRevenue = revenueData?.length ? totalRevenue / revenueData?.length : 0;
    
    const summaryData = [
      ['Total Revenue', `$${totalRevenue?.toLocaleString()}`, '100%'],
      ['Total Expenses', `$${totalExpenses?.toLocaleString()}`, `${totalRevenue ? ((totalExpenses / totalRevenue) * 100)?.toFixed(1) : 0}%`],
      ['Total Profit', `$${totalProfit?.toLocaleString()}`, `${totalRevenue ? ((totalProfit / totalRevenue) * 100)?.toFixed(1) : 0}%`],
      ['Average Monthly Revenue', `$${Math.round(avgRevenue)?.toLocaleString()}`, revenueData?.length ? `${revenueData?.length} months` : 'N/A'],
      ['Data Source', 'Live Supabase Database', 'Real-time data']
    ];
    
    autoTable(doc, {
      head: [['Metric', 'Value', 'Percentage/Notes']],
      body: summaryData,
      startY: 85,
      headStyles: { fillColor: [59, 130, 246] },
      styles: { fontSize: 10 }
    });
    
    // Monthly Breakdown
    if (revenueData?.length > 0) {
      doc?.setFontSize(16);
      doc?.text('Monthly Performance Breakdown', 20, doc?.lastAutoTable?.finalY + 20);
      
      const monthlyData = revenueData?.map(item => [
        `${item?.month} ${new Date()?.getFullYear()}`,
        `$${item?.revenue?.toLocaleString()}`,
        `$${item?.expenses?.toLocaleString()}`,
        `$${item?.profit?.toLocaleString()}`,
        item?.revenue > 0 ? `${((item?.profit / item?.revenue) * 100)?.toFixed(1)}%` : '0%'
      ]);
      
      autoTable(doc, {
        head: [['Month', 'Revenue', 'Expenses', 'Profit', 'Margin']],
        body: monthlyData,
        startY: doc?.lastAutoTable?.finalY + 30,
        headStyles: { fillColor: [59, 130, 246] },
        styles: { fontSize: 10 }
      });
    }
    
    // Footer
    doc?.setFontSize(10);
    doc?.text('Generated from live Supabase data - Revenue Chart Module', 20, doc?.internal?.pageSize?.height - 20);
    
    doc?.save(`Revenue_Chart_Report_Live_${new Date()?.toISOString()?.split('T')?.[0]}.pdf`);
  };

  const generateXLSReport = async () => {
    const XLSX = await import('xlsx');
    
    const wb = XLSX?.utils?.book_new();
    
    // Revenue Performance Summary Sheet
    const totalRevenue = revenueData?.reduce((sum, item) => sum + item?.revenue, 0);
    const totalExpenses = revenueData?.reduce((sum, item) => sum + item?.expenses, 0);
    const totalProfit = totalRevenue - totalExpenses;
    
    const summaryData = [
      ['Revenue Performance Report (Live Data)'],
      [`Generated: ${new Date()?.toLocaleDateString('en-US', { 
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      })}`],
      [`Data Period: ${timeRange}`],
      [`Organization: ${userProfile?.organization_name || 'Current Organization'}`],
      [''],
      ['REVENUE PERFORMANCE SUMMARY'],
      ['Metric', 'Value', 'Percentage/Notes'],
      ['Total Revenue', totalRevenue, '100%'],
      ['Total Expenses', totalExpenses, totalRevenue ? ((totalExpenses / totalRevenue) * 100)?.toFixed(1) + '%' : '0%'],
      ['Total Profit', totalProfit, totalRevenue ? ((totalProfit / totalRevenue) * 100)?.toFixed(1) + '%' : '0%'],
      ['Average Monthly Revenue', revenueData?.length ? Math.round(totalRevenue / revenueData?.length) : 0, `${revenueData?.length} months`],
      ['Data Source', 'Live Supabase Database', 'Real-time data'],
      [''],
      ['MONTHLY BREAKDOWN'],
      ['Month', 'Revenue', 'Expenses', 'Profit', 'Profit Margin %'],
      ...revenueData?.map(item => [
        `${item?.month} ${new Date()?.getFullYear()}`,
        item?.revenue,
        item?.expenses,
        item?.profit,
        item?.revenue > 0 ? ((item?.profit / item?.revenue) * 100)?.toFixed(1) : 0
      ])
    ];
    
    const summaryWS = XLSX?.utils?.aoa_to_sheet(summaryData);
    XLSX?.utils?.book_append_sheet(wb, summaryWS, 'Revenue Performance');
    
    // Write file
    XLSX?.writeFile(wb, `Revenue_Chart_Report_Live_${new Date()?.toISOString()?.split('T')?.[0]}.xlsx`);
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload?.length) {
      return (
        <div className="bg-popover border border-border rounded-lg p-3 shadow-elevated">
          <p className="font-medium text-foreground mb-2">{label} {new Date()?.getFullYear()}</p>
          {payload?.map((entry, index) => (
            <div key={index} className="flex items-center space-x-2">
              <div 
                className="w-3 h-3 rounded-full" 
                style={{ backgroundColor: entry?.color }}
              />
              <span className="text-sm text-muted-foreground capitalize">{entry?.dataKey}:</span>
              <span className="text-sm font-medium text-foreground">
                ${entry?.value?.toLocaleString()}
              </span>
            </div>
          ))}
        </div>
      );
    }
    return null;
  };

  const renderDownloadModal = () => {
    if (!showDownloadModal) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-card rounded-lg shadow-xl w-full max-w-md">
          <div className="p-6 border-b border-border">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-foreground flex items-center">
                <Icon name="Download" size={20} className="mr-2" />
                Download Revenue Performance
              </h3>
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => setShowDownloadModal(false)}
                disabled={downloadLoading}
              >
                <Icon name="X" size={16} />
              </Button>
            </div>
          </div>
          
          <div className="p-6">
            <p className="text-muted-foreground mb-6">
              Export your live revenue performance data including revenue trends, expense analysis, and profitability metrics from your Supabase database.
            </p>
            
            <div className="space-y-4">
              <Button
                variant="default"
                onClick={() => handleDownloadReport('pdf')}
                disabled={downloadLoading}
                className="w-full flex items-center justify-center space-x-2 h-12"
                iconName="FileText"
                iconPosition="left"
              >
                {downloadLoading ? 'Generating PDF...' : 'Download PDF Report'}
              </Button>
              
              <Button
                variant="outline"
                onClick={() => handleDownloadReport('xls')}
                disabled={downloadLoading}
                className="w-full flex items-center justify-center space-x-2 h-12"
                iconName="Sheet"
                iconPosition="left"
              >
                {downloadLoading ? 'Generating Excel...' : 'Download Excel Report'}
              </Button>
            </div>
            
            <div className="mt-6 p-4 bg-muted/50 rounded-lg">
              <div className="flex items-start space-x-2">
                <Icon name="Info" size={16} className="text-muted-foreground mt-0.5" />
                <div className="text-sm text-muted-foreground">
                  <p className="font-medium mb-1">Live revenue report includes:</p>
                  <ul className="list-disc list-inside space-y-1">
                    <li>Real-time monthly revenue and expense data</li>
                    <li>Profit calculations from actual transactions</li>
                    <li>Growth metrics and performance indicators</li>
                    <li>Data sourced from Supabase invoices and bills</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="bg-card border border-border rounded-lg shadow-professional">
        <div className="p-6 border-b border-border">
          <h2 className="text-lg font-semibold text-foreground">Financial Performance</h2>
          <p className="text-sm text-muted-foreground mt-1">Loading live data...</p>
        </div>
        <div className="p-6">
          <div className="flex items-center justify-center h-80">
            <div className="flex items-center space-x-3">
              <Icon name="Loader2" size={24} className="animate-spin text-primary" />
              <span className="text-lg text-muted-foreground">Loading revenue data...</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-card border border-border rounded-lg shadow-professional">
        <div className="p-6 border-b border-border">
          <h2 className="text-lg font-semibold text-foreground">Financial Performance</h2>
          <p className="text-sm text-error mt-1">{error}</p>
        </div>
        <div className="p-6">
          <div className="flex items-center justify-center h-80">
            <div className="text-center">
              <Icon name="AlertCircle" size={24} className="text-error mx-auto mb-4" />
              <Button onClick={loadRevenueData} variant="outline">
                <Icon name="RefreshCw" size={16} />
                Retry
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="bg-card border border-border rounded-lg shadow-professional">
        <div className="p-6 border-b border-border">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold text-foreground">Financial Performance</h2>
              <p className="text-sm text-muted-foreground mt-1">Live data from Supabase database</p>
            </div>
            <div className="flex items-center space-x-2">
              <div className="flex bg-muted/50 rounded-lg p-1">
                <button
                  onClick={() => setChartType('line')}
                  className={`px-3 py-1 rounded text-sm font-medium transition-colors duration-150 ${
                    chartType === 'line' ?'bg-primary text-primary-foreground' :'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  <Icon name="TrendingUp" size={14} className="mr-1" />
                  Trends
                </button>
                <button
                  onClick={() => setChartType('bar')}
                  className={`px-3 py-1 rounded text-sm font-medium transition-colors duration-150 ${
                    chartType === 'bar' ?'bg-primary text-primary-foreground' :'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  <Icon name="BarChart3" size={14} className="mr-1" />
                  Compare
                </button>
              </div>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => setShowDownloadModal(true)}
                disabled={downloadLoading || loading}
                title="Download Revenue Performance Report"
              >
                <Icon name="Download" size={16} />
              </Button>
              <Button 
                variant="ghost" 
                size="sm"
                onClick={loadRevenueData}
                disabled={loading}
              >
                <Icon name="RefreshCw" size={16} className={loading ? 'animate-spin' : ''} />
              </Button>
            </div>
          </div>
        </div>
        <div className="p-6">
          <div className="h-80 w-full">
            {revenueData?.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                {chartType === 'line' ? (
                  <LineChart data={revenueData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                    <XAxis 
                      dataKey="month" 
                      stroke="var(--color-muted-foreground)"
                      fontSize={12}
                    />
                    <YAxis 
                      stroke="var(--color-muted-foreground)"
                      fontSize={12}
                      tickFormatter={(value) => `$${(value / 1000)?.toFixed(0)}k`}
                    />
                    <Tooltip content={<CustomTooltip />} />
                    <Line 
                      type="monotone" 
                      dataKey="revenue" 
                      stroke="var(--color-primary)" 
                      strokeWidth={3}
                      dot={{ fill: 'var(--color-primary)', strokeWidth: 2, r: 4 }}
                      activeDot={{ r: 6, stroke: 'var(--color-primary)', strokeWidth: 2 }}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="expenses" 
                      stroke="var(--color-error)" 
                      strokeWidth={2}
                      dot={{ fill: 'var(--color-error)', strokeWidth: 2, r: 3 }}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="profit" 
                      stroke="var(--color-success)" 
                      strokeWidth={2}
                      dot={{ fill: 'var(--color-success)', strokeWidth: 2, r: 3 }}
                    />
                  </LineChart>
                ) : (
                  <BarChart data={revenueData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                    <XAxis 
                      dataKey="month" 
                      stroke="var(--color-muted-foreground)"
                      fontSize={12}
                    />
                    <YAxis 
                      stroke="var(--color-muted-foreground)"
                      fontSize={12}
                      tickFormatter={(value) => `$${(value / 1000)?.toFixed(0)}k`}
                    />
                    <Tooltip content={<CustomTooltip />} />
                    <Bar dataKey="revenue" fill="var(--color-primary)" radius={[2, 2, 0, 0]} />
                    <Bar dataKey="expenses" fill="var(--color-error)" radius={[2, 2, 0, 0]} />
                    <Bar dataKey="profit" fill="var(--color-success)" radius={[2, 2, 0, 0]} />
                  </BarChart>
                )}
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-full">
                <div className="text-center">
                  <Icon name="BarChart3" size={48} className="text-muted-foreground mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-foreground mb-2">No Revenue Data Available</h3>
                  <p className="text-sm text-muted-foreground mb-4 max-w-md">
                    Revenue chart requires invoice and bill data to display financial performance trends. 
                    Start by creating invoices and recording bills to see your revenue analytics.
                  </p>
                  <div className="space-y-2">
                    <p className="text-xs text-muted-foreground">To generate revenue data:</p>
                    <div className="flex flex-wrap gap-2 justify-center">
                      <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded-full">Create Invoices</span>
                      <span className="px-2 py-1 bg-red-100 text-red-700 text-xs rounded-full">Record Bills</span>
                      <span className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded-full">Track Payments</span>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="flex items-center justify-center space-x-6 mt-4 pt-4 border-t border-border">
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 bg-primary rounded-full" />
              <span className="text-sm text-muted-foreground">Revenue</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 bg-error rounded-full" />
              <span className="text-sm text-muted-foreground">Expenses</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 bg-success rounded-full" />
              <span className="text-sm text-muted-foreground">Profit</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 bg-blue-500 rounded-full" />
              <span className="text-sm text-muted-foreground">
                {revenueData?.length > 0 ? 'Live Data' : 'No Data'}
              </span>
            </div>
          </div>

          {/* Data source indicator */}
          <div className="mt-4 pt-4 border-t border-border">
            <div className="flex items-center justify-center">
              <div className="flex items-center space-x-2 text-xs text-muted-foreground">
                <Icon name="Database" size={12} />
                <span>
                  {revenueData?.length > 0 
                    ? 'Real-time data from Supabase • Invoices & Bills' :'Connected to Supabase • Waiting for invoice/bill data'
                  }
                </span>
                <span>•</span>
                <span>Last updated: {new Date()?.toLocaleTimeString()}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Download Modal */}
      {renderDownloadModal()}
    </>
  );
};

export default RevenueChart;